import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { useModalize } from 'react-native-modalize'

import InfoIcon from '@common/assets/svg/InfoIcon'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import flexbox from '@common/styles/utils/flexbox'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'
import { DCA_CHAIN_ID } from '@web/modules/swap-and-bridge/hooks/useDca'

import { getDcaManageErrorText } from '../errorText'
import { SIGNS_IN_OVERLAY } from '../signsInOverlay'
import DcaOrderCard from './DcaOrderCard'
import DcaOrderModal from './DcaOrderModal'
import { DcaHistoryTab, filterOrdersByTab, matchesSearch } from './orderView'
import { DcaTabs } from './parts'
import { UseDcaHistoryReturn } from './useDcaHistory'

type Props = {
  history: UseDcaHistoryReturn
  search: string
  /** Activity's network filter; `null` = all networks. */
  chainId: bigint | null
}

/** Activity → DCA: All / Active / Past tabs, one card per order, the order sheet. */
const DcaHistory = ({ history, search, chainId }: Props) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [tab, setTab] = useState<DcaHistoryTab>('all')
  const { ref: sheetRef, open: openSheet, close: closeSheet } = useModalize()
  const {
    orders,
    tokens,
    ordersStatus,
    pendingSubmissions,
    detailOrderId,
    closeDetail,
    openDetail
  } = history

  const isCancelInFlight =
    history.cancelStatus === 'preparing' || history.cancelStatus === 'running'

  // On mobile-web and in the extension popup each cancel transaction is signed
  // in SignAccountOpOverlay (see SIGNS_IN_OVERLAY). Step aside while a
  // transaction waits for its signature; the order stays open for afterwards.
  const { currentUserRequest } = useRequestsControllerState()
  const isSigningInOverlay =
    SIGNS_IN_OVERLAY && isCancelInFlight && currentUserRequest?.kind === 'calls'
  const isSigningInOverlayRef = useRef(isSigningInOverlay)
  isSigningInOverlayRef.current = isSigningInOverlay

  // An order opened from outside (the "View order" route) or closed by the
  // controller. A tap on a card opens the sheet itself (handleOpen): the id may
  // be unchanged, so this effect would not fire.
  useEffect(() => {
    if (detailOrderId && !isSigningInOverlay) openSheet()
    else closeSheet()
  }, [detailOrderId, isSigningInOverlay, openSheet, closeSheet])

  const handleOpen = useCallback(
    (orderId: string) => {
      openDetail(orderId)
      openSheet()
    },
    [openDetail, openSheet]
  )

  // The open order lives in the background controller: leaving the screen
  // without closing the sheet must not leave it set for the next visit.
  const closeDetailRef = useRef(closeDetail)
  closeDetailRef.current = closeDetail
  useEffect(() => () => closeDetailRef.current(), [])

  const handleClose = useCallback(() => {
    // Hidden for the signature, not closed by the user (onClosed still fires).
    if (isSigningInOverlayRef.current) return
    if (detailOrderId) closeDetail()
  }, [detailOrderId, closeDetail])

  // DCA runs on PulseChain only: another network selected means nothing to show.
  const onDcaChain = chainId === null || Number(chainId) === DCA_CHAIN_ID

  const visible = useMemo(
    () =>
      onDcaChain
        ? filterOrdersByTab(orders, tab).filter((o) => matchesSearch(o, tokens, search))
        : [],
    [onDcaChain, orders, tab, tokens, search]
  )

  const box = (children: React.ReactNode, testID: string) => (
    <View
      testID={testID}
      style={{
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: theme.homeCardBorder as string,
        borderRadius: RADII.md,
        paddingVertical: 40,
        paddingHorizontal: 24,
        alignItems: 'center',
        gap: 8
      }}
    >
      {children}
    </View>
  )

  const list = (() => {
    if (ordersStatus === 'loading' && !orders.length)
      return box(<Spinner style={{ width: 28, height: 28 }} />, 'dca-history-loading')
    if (ordersStatus === 'error')
      return box(
        <>
          <InfoIcon width={28} height={28} color={theme.errorDecorative as string} />
          <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
            {getDcaManageErrorText(history.ordersError ?? 'GENERIC', t)}
          </Text>
          <Text
            fontSize={14}
            weight="semiBold"
            color={theme.linkText as string}
            onPress={history.reload}
          >
            {t('Try again')}
          </Text>
        </>,
        'dca-history-error'
      )
    if (!visible.length)
      return box(
        <>
          <InfoIcon width={28} height={28} color={theme.infoDecorative as string} />
          <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
            {!onDcaChain
              ? t('DCA is only available on PulseChain.')
              : search.trim()
                ? t('No DCA orders match your search.')
                : tab === 'active'
                  ? t('No active DCA orders.')
                  : tab === 'past'
                    ? t('No past DCA orders.')
                    : t('No DCA orders yet. Set one up from the DCA tab on Swap.')}
          </Text>
        </>,
        'dca-history-empty'
      )
    return (
      <View style={{ gap: 12 }} testID="dca-history-list">
        {visible.map((order) => (
          <DcaOrderCard
            key={order.orderId}
            order={order}
            tokens={tokens}
            onPress={handleOpen}
            isCancelling={isCancelInFlight && history.cancelOrderId === order.orderId}
          />
        ))}
      </View>
    )
  })()

  return (
    <View style={{ gap: 16 }}>
      <DcaTabs<DcaHistoryTab>
        testIDPrefix="dca-history-tab"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'all', label: t('All DCAs') },
          { value: 'active', label: t('Active DCAs') },
          { value: 'past', label: t('Past DCAs') }
        ]}
      />

      {onDcaChain && tab !== 'past' && !!pendingSubmissions.length && (
        <View
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            {
              gap: 10,
              padding: 12,
              borderRadius: RADII.md,
              backgroundColor: theme.infoBackground as string
            }
          ]}
          testID="dca-history-pending"
        >
          <Spinner style={{ width: 16, height: 16 }} />
          <Text fontSize={13} weight="medium" style={{ flex: 1 }}>
            {t('Confirming {{count}} DCA transactions...', { count: pendingSubmissions.length })}
          </Text>
        </View>
      )}

      {list}

      <DcaOrderModal
        sheetRef={sheetRef}
        history={history}
        onClose={handleClose}
        // Mounted with an order already open: the sheet ref is not set yet when
        // the effect above runs.
        autoOpen={!!detailOrderId}
        hidden={isSigningInOverlay}
      />
    </View>
  )
}

export default React.memo(DcaHistory)
